import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import { useTranslation, Trans } from 'react-i18next';
import Currencies from '../../../../utils/Currencies';
import DealPrice from '../../../DealPrice.react';
import { Dates as DateUtils } from 'arbitrip-common/client/utils';
import Config from 'arbitrip-common/client/utils/Config';
import ContentLoader from 'react-content-loader';
import { Button, Tooltip } from '@mui/material';
import ChangeClientPriceDialog from '../../../general/ChangeClientPriceDialog.react';
import InstallmentsBanner from '../../../InstallmentsBanner.react';
import { getPriceDebugTooltipContent, getMaxPointsForDeal } from 'arbitrip-common/client/utils/PointsUtils';
import { isElalCompany, isIsracardCompany } from '@/utils/isCompany';
import { computeBnplAmounts } from '@/utils/PayLater';
import { MAIN_BUSINESS_LINE } from '@arbitrip/constants';
import { PriceToChargeInfoBlock } from './PriceToChargeInfoBlock';
import { ElalPriceToChargeInfoBlock } from './ElalPriceToChargeInfoBlock';
import BadgeOfferIsracard from '../../../general/BadgeOfferIsracard';
import { InfoOutlined } from '@mui/icons-material';
import ExchangeRateInfo from './ExchangeRateInfo';

const content_loader_width = 235;
const content_loader_height = 32;
const MIN_NON_ZERO_AMOUNT_TO_DISPLAY = 0.1;

function isExtraChargeExcluded(extraCharge) {
    return extraCharge && extraCharge.excluded && !extraCharge._ignored_exclusion;
}

function formatPrice(price) {
    if (isNaN(price)) {
        return 'NaN';
    }

    // Treat tiny float remainders as zero (before rounding), otherwise round to 2 decimals
    // Examples:
    // - 0.004 -> 0
    // - 5.25777 -> 5.26
    if (Math.abs(price) < MIN_NON_ZERO_AMOUNT_TO_DISPLAY) {
        return '0';
    }

    const rounded = Math.round(price * 100) / 100;

    const isWholeNumber = rounded % 1 === 0;

    if (isWholeNumber) {
        return rounded.toLocaleString();
    }

    return rounded.toFixed(2);
}

function BnplSidebarSection({ t, reservation, DEAL, applied_points_discount_value, coupon_discount_value = 0, points_display_string, selectedPaymentMethod, isConfirmation, currency, useHighExchangeRate = false }) {
    const isBnplSelected = selectedPaymentMethod === 'bnpl';
    const bnplIlsRateFormatted = reservation.bnpl_ils_rate
        ? parseFloat(reservation.bnpl_ils_rate).toFixed(5)
        : null;

    // When BNPL is selected, no credit card charge happens immediately — points cover the "now" side.
    // When Pay Now is selected, the credit card is charged the remaining deal amount after all discounts
    // (coupon + points), so the figure matches the Total Price (Client − coupon − points).
    const payNowCreditCardAmount = isBnplSelected
        ? 0
        : Math.max(
              0,
              Math.round(
                  ((DEAL.totalPrice || 0) - (coupon_discount_value || 0) - (applied_points_discount_value || 0)) * 100,
              ) / 100,
          );

    const confirmationPayLaterUsd = _.get(reservation, 'payment.bnpl.usd_amount_with_fee');
    const rawPayLaterUsd =
        isConfirmation && _.isFinite(confirmationPayLaterUsd)
            ? confirmationPayLaterUsd
            : reservation.bnpl_usd_amount_with_fee;

    // Adjust Pay Later amount for discounts: both coupon and points reduce the BNPL base before the fee is applied.
    // For the BNPL (ElAl/Isracard) company DEAL.currency is ILS, and both coupon_discount_value (ElAl coupons are
    // emitted in ILS) and applied_points_discount_value are already in ILS — same units as bnpl_ils_amount — so they
    // are summed directly with no FX conversion.
    const sidebarTotalDiscountIls = coupon_discount_value + (applied_points_discount_value || 0);
    const bnplAmounts = computeBnplAmounts({
        bnplIlsAmount: reservation.bnpl_ils_amount,
        bnplIlsRate: reservation.bnpl_ils_rate,
        bnplFeePercentage: reservation.bnpl_fee_percentage ?? 2,
        pointsDiscountIls: sidebarTotalDiscountIls,
    });
    // Post-booking, prefer the server-persisted payment.bnpl.usd_amount_with_fee (the authoritative charge, net of
    // coupon + points) so the sidebar matches the confirmation banner and the actual charge. Pre-booking there is no
    // persisted value yet, so use the client-side recompute (coupon + points).
    const adjustedPayLaterUsd =
        isConfirmation && _.isFinite(confirmationPayLaterUsd)
            ? confirmationPayLaterUsd
            : bnplAmounts && bnplAmounts.payLaterUsd != null
              ? bnplAmounts.payLaterUsd
              : rawPayLaterUsd;

    const chargeDate = reservation.bnpl_charge_date
        ? moment(reservation.bnpl_charge_date).format('DD MMM YYYY')
        : null;

    return (
        <div className="bnpl-sidebar-section">
            <div className="bnpl-sidebar-box">
                <div className="bnpl-sidebar-row bnpl-sidebar-row--header">
                    <span>{t('BnplSidebar.PayNow')}</span>
                </div>
                <div className="bnpl-sidebar-row">
                    <span className="bnpl-sidebar-label">{t('BnplSidebar.CreditCard')}</span>
                    <span className="bnpl-sidebar-value">{currency}{payNowCreditCardAmount.toFixed(0)}</span>
                </div>
                {points_display_string && (
                    <div className="bnpl-sidebar-row">
                        <span className="bnpl-sidebar-label">{t('BnplSidebar.Points')}</span>
                        <span className="bnpl-sidebar-value">{points_display_string}</span>
                    </div>
                )}
                {bnplIlsRateFormatted && !isBnplSelected && (
                    <div className="bnpl-sidebar-disclaimer">
                        <div className="bnpl-sidebar-exchange-rate">
                            <div>{t('MoreInfoSection.ContentTitle')}</div>
                            <div>
                                {t('MoreInfoSection.ExchangeRateFormat', {
                                    sourceCoin: 'ILS',
                                    targetCoin: 'USD',
                                    rate: bnplIlsRateFormatted,
                                })}
                            </div>
                        </div>
                        {useHighExchangeRate && (
                            <div className="bnpl-sidebar-exchange-desc">{t('MoreInfoSection.FXDisclosure')}</div>
                        )}
                        <div className="bnpl-sidebar-exchange-desc">{t('MoreInfoSection.Description')}</div>
                    </div>
                )}
            </div>
            {isBnplSelected && (
                <div className="bnpl-sidebar-box">
                    <div className="bnpl-sidebar-row bnpl-sidebar-row--header">
                        <span>{t('BnplSidebar.PayLater')}</span>
                    </div>
                    {adjustedPayLaterUsd != null && (
                        <div className="bnpl-sidebar-row">
                            <span className="bnpl-sidebar-label">{t('BnplSidebar.CreditCard')}</span>
                            <span className="bnpl-sidebar-value">${Math.round(adjustedPayLaterUsd)}</span>
                        </div>
                    )}
                    {chargeDate && (
                        <div className="bnpl-sidebar-description">
                            <Trans
                                i18nKey="BnplSidebar.PayLaterDesc"
                                values={{ date: chargeDate }}
                                components={{ bold: <strong className="bnpl-sidebar-description--bold" /> }}
                            />
                        </div>
                    )}
                    <div className="bnpl-sidebar-description-gray">
                        {t('BnplSidebar.PriceIncludesDisclaimer')}
                    </div>
                </div>
            )}
        </div>
    );
}

const PriceSummary = ({
    reservation,
    profile,
    hotel,
    couponValidation,
    points,
    contractClientCompanyId,
    initializing = false,
    canChangeChargeCurrency = false,
    agencyClientPriceEnabled = false,
    shouldLock = false,
    initialized = false,
    arbitripPointsApplied = false,
    onUpdateChangeClientPrice,
    hideTermsOfSupplierDisclaimer = false,
    isConfirmation = false,
    selectedPaymentMethod,
}) => {
    const { t } = useTranslation();
    const isElal = isElalCompany();
    const isIsracard = isIsracardCompany();
     const isB2C = profile?.company?.main_business_line === MAIN_BUSINESS_LINE.B2C;
    const [changeClientPriceModalOpen, setChangeClientPriceModalOpen] = React.useState(false);

    if (_.isEmpty(reservation?.deal)) {
        return null;
    }

    const HOTEL_IN_ISRAEL = hotel?.country_code === 'IL'; // exclude Eilat
    const ISRAEL_VAT_DISCLAIMER = HOTEL_IN_ISRAEL && hotel?.city !== 'Eilat'; // exclude Eilat

    const DEAL = reservation.deal;

    const refundableUntil = _.get(DEAL, 'dca.dealIsFullyRefundableTill')
        ? moment.utc(_.get(DEAL, 'dca.dealIsFullyRefundableTill')).startOf('day').subtract(1, 'minute').format('DD MMM YYYY HH:mm')
        : null;

    const checkIn = DateUtils.parseFormatDate(DEAL.check_in);
    const checkOut = DateUtils.parseFormatDate(DEAL.check_out);

    const CHECK_IN = checkIn.format('DD MMM YYYY');
    const CHECK_OUT = checkOut.format('DD MMM YYYY');
    const nights = checkOut.diff(checkIn, 'days');
    const NIGHTS_TOTAL = nights === 1 ? `${t('DealDetails.1NightStay')}` : `${nights} ${t('DealDetails.NightStay')}`;
    const isPrivateTravelOnly = !!_.get(profile, 'private_travel_only');
    const nightsTitle = (isPrivateTravelOnly || isElal) ? t('DealDetails.YourTripSummary') : NIGHTS_TOTAL;

    const currency = Currencies.getSymbol(DEAL.currency);
    const displayCurrency = Currencies.getSymbol(profile.display_currency);

    const {
        active: active_agent_pricing,
        client_price_total,
        client_price_per_night,
        absolute_margin_total,
    } = _.get(DEAL, 'agent_pricing', {});

    const agent_pricing_props = active_agent_pricing
        ? {
              agentCommission: absolute_margin_total,
              agentTotalPrice: client_price_total,
          }
        : {};

    const pricePerNight =
        active_agent_pricing && client_price_per_night ? Number(client_price_per_night.toFixed(2)) : DEAL.pricePerNight;

    const getPriceString = (price, currencySymbol, show_zero) => {
        if (isNaN(price)) {
            return 'NaN';
        }

        return price
            ? Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
                  price,
                  profile.display_currency_exchange_rate,
                  profile.display_currency,
              )
            : show_zero
              ? Currencies.getPriceWithDisplayCurrencyByPrecision(0, 1, profile.display_currency, 0)
              : '';
    };

    const PRICE_PER_NIGHT = getPriceString(pricePerNight, currency, false);

    const taxesAndFees = DEAL.taxes_and_fees;
    const TAXES_AND_FEES = getPriceString(taxesAndFees, DEAL.taxes_and_fees_currency, false);

    const taxServiceFee = DEAL.tax_service_fee;
    const taxServiceFeeExist = taxServiceFee && taxServiceFee > 0;
    const TAX_SERVICE_FEE = taxServiceFeeExist
        ? Currencies.getPriceWithDisplayCurrencyByPrecision(
              taxServiceFee,
              profile.display_currency_exchange_rate,
              profile.display_currency,
              0,
          )
        : null;

    const salesTax = DEAL.sales_tax;
    const salesTaxExist = salesTax && salesTax > 0;
    const SALES_TAX = salesTaxExist ? Currencies.getSymbol(DEAL.sales_tax_currency) + salesTax.toLocaleString() : null;

    const paymentsExcluded = DEAL.payments_excluded;
    const PAYMENTS_EXCLUDED =
        paymentsExcluded > 0
            ? Currencies.getSymbol(DEAL.payments_excluded_currency) + paymentsExcluded.toLocaleString()
            : null;

    const extraCharges = _.get(DEAL, 'original.details.extra_charges', []);
    const [excludedExtraCharges, includedExtraCharges] = _.partition(extraCharges, isExtraChargeExcluded);

    const renderExtraCharges = (extraCharges, excluded) => {
        if (Array.isArray(extraCharges) && extraCharges.length) {
            return (
                <React.Fragment>
                    {excluded ? (
                        <div className="flex space-between excluded-extra-charges">
                            <div>
                                <span>{t('ExcludedExtraCharges')}</span>
                            </div>
                        </div>
                    ) : null}
                    {extraCharges.map((extra_charge, idx) => {
                        const extra_charge_adapted_price = extra_charge.other_currency
                            ? Currencies.getPriceWithDisplayCurrencyByPrecision(
                                  extra_charge.other_currency.amount,
                                  profile.display_currency_exchange_rate,
                                  profile.display_currency,
                                  0,
                              )
                            : profile.currency === extra_charge.currency
                              ? Currencies.getPriceWithDisplayCurrencyByPrecision(
                                    extra_charge.amount,
                                    profile.display_currency_exchange_rate,
                                    profile.display_currency,
                                    0,
                                )
                              : Currencies.getRoundedPriceWithCurrency(extra_charge.amount, extra_charge.currency);

                        return (
                            <div className="flex space-between extra-charge" key={idx}>
                                <div title={Config.dev_mode && extra_charge.type}>
                                    {!excluded && idx === 0 ? <span>(</span> : null}
                                    {extra_charge.name}
                                </div>
                                <div>
                                    {extra_charge_adapted_price}
                                    {!excluded && idx === extraCharges.length - 1 ? <span>)</span> : null}
                                </div>
                            </div>
                        );
                    })}
                </React.Fragment>
            );
        }

        return null;
    };

    const INCLUDED_EXTRA_CHARGES = includedExtraCharges.length ? renderExtraCharges(includedExtraCharges, false) : null;
    const EXCLUDED_EXTRA_CHARGES = excludedExtraCharges.length ? renderExtraCharges(excludedExtraCharges, true) : null;

    const postPay = DEAL.post_pay;
    const hotelPrice = DEAL.hotel_price;
    const hotelCurrency = DEAL.hotel_currency;
    const HOTEL_PRICE =
        hotelCurrency && hotelPrice ? Currencies.getRoundedPriceWithCurrency(hotelPrice, hotelCurrency) : null;

    const bank_balance = _.get(profile, 'bank.balance', 0);
    const exact_points_applied = arbitripPointsApplied ? Math.min(DEAL.totalPrice, bank_balance) : 0;

    const user_chosen_points = _.get(points, 'user_chosen_points', 0);

    // Get points exchange rate
    const points_ex_rate_from_points = _.get(points, 'points_ex_rate');
    const has_points_ex_rate = points_ex_rate_from_points && points_ex_rate_from_points > 0;

    const arbitrip_points_to_currency_exchange_rate = _.get(
        profile,
        'loyalty.arbitrip_points_to_currency_exchange_rate',
    );

    // Calculate exchange rate with fallback logic
    let points_exchange_rate;
    if (arbitrip_points_to_currency_exchange_rate) {
        // for Arbitrip points
        points_exchange_rate = arbitrip_points_to_currency_exchange_rate;
    } else if (has_points_ex_rate) {
        // for others points
        points_exchange_rate = points_ex_rate_from_points;
    } else {
        points_exchange_rate = 0;
    }

    // Calculate points discount value for price reduction
    // Priority: use exact_points_applied (in currency) if available, otherwise convert user_chosen_points (in points) to currency
    const points_discount_value =
        exact_points_applied > 0
            ? exact_points_applied
            : user_chosen_points > 0
              ? user_chosen_points * points_exchange_rate
              : 0;

    // For display: use user_chosen_points if available, otherwise convert exact_points_applied to points
    const points_to_display =
        user_chosen_points > 0
            ? user_chosen_points
            : exact_points_applied > 0 && points_exchange_rate > 0
              ? exact_points_applied / points_exchange_rate
              : 0;
    const points_display_string =
        points_to_display > 0 ? `${Math.round(points_to_display).toLocaleString('en-US')} ${t('ShortPoints')}` : null;

    // Calculate max points for deal
    const maxPointsForDeal = points && points.balance ? getMaxPointsForDeal(DEAL, profile, points) : null;

    // Coupon discount (currency major units) from validation
    const cv = couponValidation;
    let coupon_discount_value = 0;
    if (cv && cv.eligible && cv.discount) {
        const { type, amount, percent } = cv.discount;
        if (type === 'percent' && _.isFinite(percent) && percent > 0) {
            coupon_discount_value = Math.max(0, Math.round((DEAL.totalPrice * percent / 100) * 100) / 100);
        } else if (_.isFinite(amount) && amount > 0) {
            coupon_discount_value = Math.max(0, amount);
        }
    }

    // Apply coupon first, then cap points by remaining price after coupon
    const price_after_coupon = Math.max(0, DEAL.totalPrice - coupon_discount_value);
    const applied_points_discount_value = Math.min(points_discount_value, price_after_coupon);

    const isBnplSelected = selectedPaymentMethod === 'bnpl';
    // coupon_discount_value and applied_points_discount_value are already in DEAL.currency (ILS for the BNPL
    // company), matching bnpl_ils_amount — sum directly, no FX conversion.
    const totalBnplDiscountIls = coupon_discount_value + applied_points_discount_value;
    const bnplAmountsForTotal = computeBnplAmounts({
        bnplIlsAmount: reservation.bnpl_ils_amount,
        bnplIlsRate: reservation.bnpl_ils_rate,
        bnplFeePercentage: reservation.bnpl_fee_percentage ?? 2,
        pointsDiscountIls: totalBnplDiscountIls,
        pointIlsValue: points_exchange_rate,
    });
    const bnplUsdAmountForTotal = bnplAmountsForTotal ? bnplAmountsForTotal.payLaterUsd : null;

    const TOTAL_PRICE = getPriceString(
        DEAL.totalPrice - coupon_discount_value - applied_points_discount_value,
        currency,
        true,
    );

    const total_price_base = client_price_total || DEAL.totalPrice;
    const total_price_after_points = total_price_base - coupon_discount_value - applied_points_discount_value;

    const billing_price_formatted = formatPrice(total_price_after_points);
    const shouldShowBillingPrice =
        _.isFinite(total_price_after_points) && Math.abs(total_price_after_points) >= MIN_NON_ZERO_AMOUNT_TO_DISPLAY;
    const TOTAL_BILLING_PRICE = Currencies.getSymbol(currency) + billing_price_formatted;

    // TODO: double check with three currencies scenario
    const hasDifferentDisplayCurrency =
        postPay && DEAL.hotel_currency
            ? DEAL.hotel_currency !== profile.display_currency
            : DEAL.currency !== profile.display_currency;

    const payNowUsdAmountForTotal =
        !isBnplSelected && bnplAmountsForTotal ? bnplAmountsForTotal.payNowUsd : null;

    const PRICE_CONVERTED_DISCLAIMER = t('ThisPriceIsConvertedToShow', { currency: displayCurrency });

    //// You will be / have been charged section ////
    // Exchange rate info shows when charge currency differs from display currency:
    // always for El Al, and for anyone when the Bank Hapoalim high exchange rate flag is on.
    // The "higher Hapoalim rate" disclaimer inside is gated separately by the same flag.
    const useHighExchangeRateEnabled = !!profile?.company?.settings?.use_high_exchange_rate;
    const shouldShowExchangeRateInfo = hasDifferentDisplayCurrency && ( isElal || useHighExchangeRateEnabled );
    // Billing row is hidden when exchange rate info is shown, on post-pay...
    const shouldRenderBilling = !shouldShowExchangeRateInfo && hasDifferentDisplayCurrency && !postPay && !canChangeChargeCurrency && shouldShowBillingPrice;
    const activeBillingConfig = shouldRenderBilling && 
        {
            label: t(isConfirmation ? 'DealDetails.YouHaveBeenCharged' : 'DealDetails.YouWillBeCharged'),
            className: `billing-price-wrap flex space-between ${isIsracard || isElal ? 'elal' : ''}`.trim(),
            priceElement: <b>{TOTAL_BILLING_PRICE}</b>,
        }
    
    // Render coupon banner with normalized discount object
    const renderCouponDiscountBanner = () => {
        const eligible = cv && cv.eligible;
        const discount = cv && cv.discount;
        if (!eligible || !discount) return null;

        let amountText = '';
        if (discount.type === 'percent' && _.isFinite(discount.percent)) {
            amountText = `${discount.percent}%`;
        } else if (_.isFinite(discount.amount) && discount.amount > 0) {
            const dealCurrency = _.get(reservation, 'deal.currency');
            const amountMajor = Math.max(0, discount.amount || 0);
            amountText = `${Currencies.getSymbol(dealCurrency)}${amountMajor.toLocaleString()}`;
        }
        if (!amountText) return null;

        return (
            <div className="coupon-discount-banner">
                <Trans
                    i18nKey="YouSavedMoneyWithCoupon"
                    values={{ amount: amountText, coupon: cv.code || '' }}
                    components={{ bold: <b />, br: <br /> }}
                />
            </div>
        );
    };

    const handleClickChangeClientPrice = () => {
        setChangeClientPriceModalOpen(true);
    };

    const handleCancelChangeClientPrice = () => {
        setChangeClientPriceModalOpen(false);
    };

    const handleUpdateChangeClientPrice = (agentPricing) => {
        if (onUpdateChangeClientPrice) {
            onUpdateChangeClientPrice(agentPricing);
        }
        setChangeClientPriceModalOpen(false);
    };

    return (
        <div className="deal-details" data-testid="deal-details">
            <span className="nights">{nightsTitle}</span>
            <div className="dates-container">
                <div className="flex flex-column check-in">
                    <span>{t('DealDetails.CheckIn')}</span>
                    <span data-testid="check-in-date">{CHECK_IN}</span>
                </div>
                <div className="flex flex-column check-out">
                    <span>{t('DealDetails.CheckOut')}</span>
                    <span data-testid="check-out-date">{CHECK_OUT}</span>
                </div>
            </div>
            {isPrivateTravelOnly || isElal ? (
                <div className="flex space-between average-price-container">
                    <span>{NIGHTS_TOTAL}</span>
                    <span />
                </div>
            ) : (
                <div className="flex space-between average-price-container">
                    <span>{t('DealDetails.AvgRoomNight')}</span>
                    <span data-testid="average-price" className="price-per-night">{PRICE_PER_NIGHT}</span>
                </div>
            )}
            {/* Coupon discount banner */}
            {renderCouponDiscountBanner()}
            {isElal ? (
                <ElalPriceToChargeInfoBlock
                    t={t}
                    hasDifferentDisplayCurrency={hasDifferentDisplayCurrency}
                    profile={profile}
                    DEAL={DEAL}
                    agent_pricing_props={agent_pricing_props}
                    contractClientCompanyId={contractClientCompanyId}
                    applied_points_discount_value={applied_points_discount_value}
                    nights={nights}
                    pricePerNight={pricePerNight}
                    active_agent_pricing={active_agent_pricing}
                    client_price_total={client_price_total}
                    absolute_margin_total={absolute_margin_total}
                    points={points}
                    maxPointsForDeal={maxPointsForDeal}
                    user_chosen_points={user_chosen_points}
                    points_to_display={points_to_display}
                    points_display_string={points_display_string}
                    TOTAL_PRICE={TOTAL_PRICE}
                    isBnplSelected={isBnplSelected}
                    bnplUsdAmount={bnplUsdAmountForTotal}
                    payNowUsdAmount={payNowUsdAmountForTotal}
                />
            ) : (
                <PriceToChargeInfoBlock
                    t={t}
                    hasDifferentDisplayCurrency={hasDifferentDisplayCurrency}
                    profile={profile}
                    DEAL={DEAL}
                    agent_pricing_props={agent_pricing_props}
                    contractClientCompanyId={contractClientCompanyId}
                    HOTEL_IN_ISRAEL={HOTEL_IN_ISRAEL}
                    TOTAL_PRICE={TOTAL_PRICE}
                />
            )}
            {reservation?.bnpl_eligible && (
                <BnplSidebarSection
                    t={t}
                    reservation={reservation}
                    DEAL={DEAL}
                    applied_points_discount_value={applied_points_discount_value}
                    coupon_discount_value={coupon_discount_value}
                    points_display_string={points_display_string}
                    selectedPaymentMethod={selectedPaymentMethod}
                    isConfirmation={isConfirmation}
                    currency={currency}
                    useHighExchangeRate={useHighExchangeRateEnabled}
                />
            )}
            {taxServiceFeeExist ? (
                <div className="flex flex-end tax-info">
                    <div>
                        <span>(</span>
                        <span className="tax-service-fee">{t('DealDetails.IncludedTaxesAndFees')}</span>
                    </div>
                    &nbsp;
                    <div>{TAX_SERVICE_FEE})</div>
                </div>
            ) : null}

            {isIsracardCompany() && <BadgeOfferIsracard size="full" style={{marginTop:"8px"}} />}

            {activeBillingConfig && (
                <div className={activeBillingConfig.className}>
                    <span>{activeBillingConfig.label}</span>
                    <span>{activeBillingConfig.priceElement}</span>
                </div>
            )}

            {salesTaxExist ? (
                <div className="flex flex-end tax-info">
                    <div>
                        <span>(</span>
                        <span className="sales-tax">{t('DealDetails.IncludedSalesTax')}</span>
                    </div>
                    &nbsp;
                    <div>{SALES_TAX})</div>
                </div>
            ) : null}
            {!postPay && taxesAndFees && taxesAndFees > 0 && (
                <div className="flex flex-end tax-info">
                    <div>({t('IncludedTaxesAndFees')})</div>
                    &nbsp;
                    <div>{TAXES_AND_FEES})</div>
                </div>
            )}

            {INCLUDED_EXTRA_CHARGES}
            {EXCLUDED_EXTRA_CHARGES}

            {shouldShowExchangeRateInfo && !reservation?.bnpl_eligible && <ExchangeRateInfo
                                                        exchangeRate={profile.display_currency_exchange_rate}
                                                        totalCharge={TOTAL_BILLING_PRICE}
                                                        sourceCoin={DEAL.currency}
                                                        targetCoin={profile.display_currency}
                                                        useHighExchangeRate={useHighExchangeRateEnabled}
                                                        />
            }

            {agencyClientPriceEnabled && (
                <React.Fragment>
                    <Button
                        aria-label="change client price"
                        variant="contained"
                        color="primary"
                        className="change-client-price"
                        onClick={handleClickChangeClientPrice}
                        disabled={initializing || shouldLock}
                        disableRipple
                    >
                        {t('ChangeClientPrice')}
                    </Button>
                    {initialized && !shouldLock && (
                        <ChangeClientPriceDialog
                            dialogOpen={changeClientPriceModalOpen}
                            dialogTitle={t('ChangeClientPrice')}
                            deal={DEAL}
                            initializing={initializing}
                            disabled={shouldLock}
                            onCancel={handleCancelChangeClientPrice}
                            onUpdate={handleUpdateChangeClientPrice}
                            profile={profile}
                        />
                    )}
                </React.Fragment>
            )}
            <div className="flex flex-column flex-strech pay-container">
                {initializing ? (
                    <ContentLoader
                        width={content_loader_width}
                        height={content_loader_height}
                        viewBox={`0 0 ${content_loader_width} ${content_loader_height}`}
                    >
                        <rect x="0" y="0" rx="8" ry="8" width={content_loader_width} height={content_loader_height} />
                    </ContentLoader>
                ) : (
                    <React.Fragment>
                        {postPay &&
                            HOTEL_PRICE &&
                            (hasDifferentDisplayCurrency ? (
                                <>
                                    <div className="flex pay-wrap space-between">
                                        <div>{t('TotalInHotelsCurrency')}</div>
                                        <div>{HOTEL_PRICE}</div>
                                    </div>
                                    <div className="no-payments-excluded">
                                        {hasDifferentDisplayCurrency &&
                                            t('ThePriceIsConvertedToShow', profile.display_currency) +
                                                t('CalculatedAsDefinedInArbitripsTermsOfService')}
                                        {t('YoullPayAtHotelInCurrency', { currency: DEAL.hotel_currency })}
                                        {hasDifferentDisplayCurrency && t('TheExchangeRateMightChange')}
                                    </div>
                                </>
                            ) : (
                                <div className="pay-wrap flex-column space-between">
                                    <div className="no-payments-excluded">{t('YoullPayAtTheHotel')}</div>
                                </div>
                            ))}
                        {!postPay && paymentsExcluded > 0 && (
                            <div
                                className={`pay-wrap flex space-between${Config.research_dev_mode && DEAL.payments_excluded_raw ? ' reference-pay-wrap' : ''}`}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {t('PayAtHotel')}
                                    {isB2C && (
                                        <Tooltip title={t('PayAtHotelTooltip')}>
                                            <InfoOutlined style={{ fontSize: '14px', color: '#666' }} />
                                        </Tooltip>
                                    )}
                                </div>
                                <div className="price">{PAYMENTS_EXCLUDED}</div>
                            </div>
                        )}
                        {ISRAEL_VAT_DISCLAIMER && (
                            <div className="no-payments-excluded">
                                <b>* {t('DealDetails.VatIsNotIncluded')}</b>
                            </div>
                        )}

                        {!isConfirmation &&
                            selectedPaymentMethod !== 'bnpl' &&
                            !bnplAmountsForTotal?.isFullyCovered && <InstallmentsBanner />}

                        {refundableUntil && (
                            <div className="price-summary-refundable">
                                {t('BnplSidebar.FullyRefundableUntil', { date: refundableUntil })}
                            </div>
                        )}

                        {(hasDifferentDisplayCurrency || isBnplSelected || payNowUsdAmountForTotal != null) && !postPay ? (
                            <div className="price-converted-disclaimer">
                                {'*'} {(isBnplSelected || payNowUsdAmountForTotal != null) ? t('ThisPriceIsConvertedToShow', { currency: 'USD' }) : PRICE_CONVERTED_DISCLAIMER}
                            </div>
                        ) : null}
                        {!postPay && !paymentsExcluded && !hideTermsOfSupplierDisclaimer && (
                            <div className="no-payments-excluded">
                                {t('DealDetails.SeeTermsOfSuppliersBelowIfApplicable')}
                            </div>
                        )}
                    </React.Fragment>
                )}
            </div>
        </div>
    );
};

export default PriceSummary;
